import React from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

const Pagination = ({
  currentPage = 0,
  totalPages = 0,
  pageSize = 10,
  totalItems = 0,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = [10, 20, 50, 100],
  className = '',
  activeColorClass = 'bg-[#3b82f6] border-[#3b82f6] text-white shadow-xs',
}) => {
  // Page number calculation for desktop & mobile
  const getPageNumbers = () => {
    const pages = [];
    if (totalPages <= 5) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);

      let start = Math.max(1, currentPage - 1);
      let end = Math.min(totalPages - 2, currentPage + 1);

      if (currentPage <= 1) {
        end = 2;
      } else if (currentPage >= totalPages - 2) {
        start = totalPages - 3;
      }

      if (start > 1) {
        pages.push('ellipsis1');
      }

      for (let i = start; i <= end; i++) {
        if (!pages.includes(i)) pages.push(i);
      }

      if (end < totalPages - 2) {
        pages.push('ellipsis2');
      }

      if (!pages.includes(totalPages - 1)) {
        pages.push(totalPages - 1);
      }
    }
    return pages;
  };

  const startItem = totalItems === 0 ? 0 : currentPage * pageSize + 1;
  const endItem = Math.min((currentPage + 1) * pageSize, totalItems);

  return (
    <div className={`flex flex-row items-center justify-between gap-3 border-t border-gray-100 pt-4 mt-6 text-xs text-gray-500 font-medium ${className}`}>
      {/* Left side: Show dropdown & entries summary */}
      <div className="flex items-center gap-2 flex-shrink-0">
        <span className="hidden sm:inline font-bold uppercase tracking-wider text-[10px] text-gray-400">Show</span>
        <div className="relative flex items-center">
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange && onPageSizeChange(Number(e.target.value))}
            className="h-9 sm:h-10 pl-3 pr-8 bg-white border border-gray-200 rounded-xl text-xs sm:text-sm font-bold text-gray-800 focus:outline-none focus:border-[#3b82f6] appearance-none cursor-pointer shadow-2xs min-w-[66px]"
          >
            {pageSizeOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
          <ChevronDown size={14} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
        </div>
        <span className="hidden md:inline text-xs text-gray-500 font-semibold">
          {totalItems > 0 ? `Showing ${startItem}-${endItem} of ${totalItems}` : 'No records'}
        </span>
      </div>

      {/* Right side: Modern Rounded Card Pagination Navigation */}
      {totalPages > 0 && (
        <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
          {/* Previous Button Card */}
          <button
            type="button"
            onClick={() => onPageChange && onPageChange(currentPage - 1)}
            disabled={currentPage === 0}
            aria-label="Previous Page"
            className="w-9 h-9 sm:w-10 sm:h-10 bg-white border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-35 disabled:cursor-not-allowed transition-all flex items-center justify-center cursor-pointer shadow-2xs"
          >
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>

          {/* Numbered Page Buttons & Ellipsis Cards */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {getPageNumbers().map((pageNum, idx) => {
              if (pageNum === 'ellipsis1' || pageNum === 'ellipsis2') {
                return (
                  <div
                    key={`ell-${idx}`}
                    className="w-9 h-9 sm:w-10 sm:h-10 bg-white border border-gray-200 rounded-xl text-gray-600 font-bold text-xs sm:text-sm flex items-center justify-center select-none shadow-2xs"
                  >
                    ...
                  </div>
                );
              }
              const isActive = currentPage === pageNum;
              return (
                <button
                  key={`page-${pageNum}`}
                  type="button"
                  onClick={() => onPageChange && onPageChange(pageNum)}
                  className={`w-9 h-9 sm:w-10 sm:h-10 rounded-xl text-xs sm:text-sm font-bold border transition-all cursor-pointer flex items-center justify-center ${
                    isActive
                      ? activeColorClass
                      : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 shadow-2xs'
                  }`}
                >
                  {pageNum + 1}
                </button>
              );
            })}
          </div>

          {/* Next Button Card */}
          <button
            type="button"
            onClick={() => onPageChange && onPageChange(currentPage + 1)}
            disabled={currentPage >= totalPages - 1}
            aria-label="Next Page"
            className="w-9 h-9 sm:w-10 sm:h-10 bg-white border border-gray-200 rounded-xl text-gray-600 hover:bg-gray-50 disabled:opacity-35 disabled:cursor-not-allowed transition-all flex items-center justify-center cursor-pointer shadow-2xs"
          >
            <ChevronRight size={18} strokeWidth={2.2} />
          </button>
        </div>
      )}
    </div>
  );
};

export default Pagination;
